import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  EnterpriseSearchService,
  UserService,
  DocGender,
  Guest,
  PassengerType,
  ServiceType,
  ModalOpenerService,
  Office,
  BookerType
} from '@sabstravtech/obtservices/angular';
import {
  Traveller,
  PersonTitles,
  PersonTitle,
  Gender,
  genders,
  ages
} from '@sabstravtech/obtservices/base';
import moment from 'moment';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

import { BaseComponent } from '../../../core/components/base_components/base-componet';
import { DeviceDetector } from '../../../core/services/device-detector.service';
import { Helpers } from '../../../core/classes/helpers';
import { LightningModalTypes } from '../../../core/classes/modal-types.enum';
import { ThemeService } from '../../../core/services/theme.service';

@Component({
  selector: 'app-guest-traveller-form',
  templateUrl: './guest-traveller-form.component.html',
  styleUrls: ['./guest-traveller-form.component.scss'],
  standalone: false
})
export class GuestTravellerFormComponent extends BaseComponent implements OnInit {
  @Output() guest: EventEmitter<{ guest: Traveller; selectedOffice: string }> = new EventEmitter<{
    guest: Traveller;
    selectedOffice: string;
  }>();
  @Input() selectedTravellers: Traveller[];
  /** titles - ask for gender if Dr or Prof chosen */
  readonly titles: PersonTitle[] = PersonTitles;
  displayTitles: PersonTitle[];

  public serviceType: typeof ServiceType = ServiceType;
  titlesMap: { [key: string]: boolean } = {};

  /** genders list */
  readonly genders = genders;
  public travelersSameName = false;
  public isFlightSelected = false;
  public emailRequired = true;
  public loading = false;
  public error = '';
  public countries = [];
  private tempCountries = [
    { value: 'Honduras', code: 'HN' },
    { value: 'Guinea-Bissau', code: 'GW' }
  ];
  selectedGuest = undefined;

  // model variables
  public guest_title: PersonTitle;
  public guest_first_name = '';
  public guest_last_name = '';
  public guest_email = '';
  public guest_phone = '';
  public guest_nationality = '';
  public guestMobileCountryNo = '';
  public guestMobileNumber = '';

  public childAllowed = false;
  public isChild = false;
  public childDOB: NgbDateStruct | null = null;
  public minChildDate: NgbDateStruct | null = null;
  public maxChildDate: NgbDateStruct | null = null;

  public guestDetailsInvalid = true;
  public isInfant = false;
  public savedGuests: Guest[] = [];
  loadingSavedGuests = true;
  faTrash = faTrash;
  offices: Office[];
  selectedOffice = '_none';
  isOfficeLabel = '';
  officeName: string;
  allowGuestBookingsAcrossOffices = false;
  constructor(
    private searchService: EnterpriseSearchService,
    private userService: UserService,
    public deviceDetector: DeviceDetector,
    title: Title,
    public translateService: TranslateService,
    private modalService: ModalOpenerService,
    public themeService: ThemeService
  ) {
    super(title, translateService);
  }

  ngOnInit(): void {
    this.setTitle(
      this.translateService.instant(
        `Guest traveller form ${this.themeService.isLightningEnv ? ' - LightUk' : ''}`
      )
    );
    this.displayTitles = JSON.parse(JSON.stringify(this.titles));
    this.titlesMap = this.displayTitles.reduce(
      (current: { [key: string]: boolean }, { title: title1, gender }) => {
        current[title1] = !!gender;
        return current;
      },
      {}
    );
    this.guest_title = this.displayTitles[0];
    // if Rail service chosen - show "Traveller is a child" block
    this.childAllowed = this.searchService.search_objects[ServiceType.Rail].chosen;
    if (this.childAllowed) {
      this.setChildDates();
    }

    this.getGuests();
    const bookingType: BookerType = this.userService.getUserBookingType();
    if (
      bookingType === BookerType.Company ||
      bookingType === BookerType.Division ||
      bookingType === BookerType.Root
    ) {
      this.subscribe(
        this.searchService.getUserCompanyOffices(),
        (offices: Office[]) => {
          this.offices = offices.sort();
          this.selectedOffice = '_none';
          // Try to get the current office name or ID from userService
          const userOfficeId = this.userService.fullUserDetails?.allDetails?.value?.office?.id;
          let officeNameOrLabel: string | undefined;
          if (userOfficeId && this.offices?.length) {
            const matchedOffice = this.offices.find((o) => o.id === userOfficeId);
            if (matchedOffice) {
              officeNameOrLabel = matchedOffice.officeLabel || matchedOffice.name;
            }
          }
          this.officeName =
            officeNameOrLabel ||
            this.userService.getfullUserDetails()?.userData?.value?.office_name;
        },
        (error) => {
          console.error(`+++ Error recovering offices: ${error} +++`);
        }
      );
      this.isOfficeLabel = this.userService.fullUserDetails.allDetails.value.office.officeLabel;
      this.allowGuestBookingsAcrossOffices =
        this.userService.fullUserDetails.allDetails.value.office.allowGuestBookingsAcrossOffices;
    }
  }
  getGuests(officeId: string = null) {
    this.subscribe(
      this.userService.getUserGuests(officeId),
      (guests) => {
        const sortedGuests = guests.sort(
          (a, b) => a.forename.localeCompare(b.forename) || a.surname.localeCompare(b.surname)
        );

        if (!this.searchService.getCurrentNoOfTravellers() || !this.childAllowed) {
          this.savedGuests = sortedGuests.filter(
            (guest) => guest.guestType === PassengerType.Adult
          );
        } else {
          this.savedGuests = sortedGuests;
        }

        this.loadingSavedGuests = false;
      },
      (error) => {
        console.error(`+++ Error recovering saved guests: ${error} +++`);
        this.loadingSavedGuests = false;
      }
    );
  }

  /**
   * @desc - return wether or not the gender dropdown should be disabled, should be enabled only on non-gendered titles, e.g. Dr
   */
  disableGenderDropdown(): boolean {
    return this.titlesMap[this.guest_title.title];
  }

  /**
   * @desc - create and emit the new guest traveller object
   */
  submitGuest(): void {
    const guestOffice = this.selectedOffice === '_none' ? null : this.selectedOffice;
    this.error = '';
    if (this.selectedTravellers?.length) {
      const foundExistingTraveller = this.selectedTravellers.find(
        (traveller) =>
          (traveller.forename + traveller.surname).toLowerCase().trim() ===
          (this.guest_first_name + this.guest_last_name).toLowerCase().trim()
      );

      if (foundExistingTraveller) {
        this.error = 'Travellers with the same name not permitted under same booking.';
        console.error('+++ Traveller Exists on Trip - backing out +++');
        return;
      }
    }
    let existingGuest: Guest;
    if (this.savedGuests?.length) {
      existingGuest = this.savedGuests.find(
        (guest) =>
          guest.title === this.guest_title.title &&
          (guest.forename + guest.surname).toLowerCase().trim() ===
            (this.guest_first_name + this.guest_last_name).toLowerCase().trim() &&
          guest.email.toLowerCase() === this.guest_email.toLowerCase() &&
          guest.nationality === this.guest_nationality &&
          guest.gender === this.guest_title.gender.substring(0, 1)
      );
    }
    if (!existingGuest) {
      const childDOB = this.childDOB;
      if (childDOB) {
        childDOB.month -= 1;
      }
      const newGuest = {
        title: this.guest_title.title,
        forename: this.guest_first_name,
        surname: this.guest_last_name,
        nationality: this.guest_nationality,
        email: this.guest_email,
        gender: this.getGender(this.guest_title.gender),
        // hardcode for now as guestType is mandatory
        guestType: this.isChild ? PassengerType.Child : PassengerType.Adult,
        // this is the date-time format
        dob: this.isChild ? Helpers.formatIsoDate(moment(this.childDOB)) : null,
        countryCode: this.guestMobileCountryNo,
        phoneNumber: this.guestMobileNumber,
        officeId: guestOffice
      };

      console.log('+++ Attempting to add new guest: ', newGuest, ' +++');
      this.loading = true;
      this.subscribe(
        this.userService.createGuest(newGuest),
        (result) => {
          console.log(`+++ Successfully added guest: ${result} +++`);
          const newTraveller: Traveller = {
            isChild: result.guestType === PassengerType.Child ? true : false,
            isInfant: false,
            childDOB: null,
            id: result.id,
            forename: result.forename.trim(),
            guest: true,
            surname: result.surname.trim(),
            title: result.title.trim(),
            email: result.email.trim(),
            name: `${result.forename.trim()} ${result.surname.trim()}`,
            ageType: result.guestType === PassengerType.Child ? ages.Child : ages.Adult
          };

          // now we need to add the guest to the travellers and navigate back to the main search page
          this.guest.emit({ guest: newTraveller, selectedOffice: guestOffice });
        },
        (error) => {
          console.error(`+++ Error adding guest: ${error} +++`);
          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );
    } else {
      const newTraveller: Traveller = {
        isChild: existingGuest.guestType === PassengerType.Child ? true : false,
        isInfant: false,
        childDOB: existingGuest.dob,
        id: existingGuest.id,
        forename: existingGuest.forename.trim(),
        guest: true,
        surname: existingGuest.surname.trim(),
        title: existingGuest.title.trim(),
        email: existingGuest.email.trim(),
        name: `${existingGuest.forename.trim()} ${existingGuest.surname.trim()}`,
        ageType: existingGuest.guestType === PassengerType.Child ? ages.Child : ages.Adult
      };

      // now we need to add the guest to the travellers and navigate back to the main search page
      this.guest.emit({ guest: newTraveller, selectedOffice: guestOffice });
    }
  }
  getGender(gender: string) {
    switch (gender) {
      case Gender.Female:
        return DocGender.F;
      case Gender.Male:
        return DocGender.M;
      case Gender.Undisclosed:
        return DocGender.U;
      case Gender.Unspecified_Others:
        return DocGender.X;
      default:
        console.warn('unknown gender:', gender);
        return null;
    }
  }

  chooseSavedGuest(guestId: string) {
    const savedGuest = this.savedGuests.find((guest) => guest.id === guestId);
    if (this.selectedTravellers?.length) {
      const foundExistingGuest = this.selectedTravellers.find(
        (traveller) =>
          (traveller.forename + traveller.surname).toLowerCase().trim() ===
          (savedGuest.forename + savedGuest.surname).toLowerCase().trim()
      );

      if (foundExistingGuest) {
        this.error = 'Travellers with the same name not permitted under same booking.';
        console.error('+++ Traveller Exists on Trip - backing out +++');
        return;
      }
    }

    const newTraveller: Traveller = {
      isChild: savedGuest.guestType === PassengerType.Child ? true : false,
      isInfant: false,
      childDOB: savedGuest.dob,
      id: savedGuest.id,
      forename: savedGuest.forename,
      guest: true,
      surname: savedGuest.surname,
      title: savedGuest.title,
      email: savedGuest.email,
      name: `${savedGuest.forename} ${savedGuest.surname}`,
      ageType: savedGuest.guestType === PassengerType.Child ? ages.Child : ages.Adult
    };

    // now we need to add the guest to the travellers and navigate back to the main search page
    this.guest.emit({
      guest: newTraveller,
      selectedOffice: this.selectedOffice === '_none' ? null : this.selectedOffice
    });
  }

  setChildDates() {
    // For Rail a child ticket will need to be booked for ages 5-16
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 16);
    this.minChildDate = Helpers.dateToNgDate(minDate);

    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 5);
    this.maxChildDate = Helpers.dateToNgDate(maxDate);
  }

  close(): void {
    this.travelersSameName = false;
  }

  deleteSavedGuest(guestData) {
    this.modalService
      .open(
        LightningModalTypes.BasicModalComponent,
        { centered: true, backdrop: 'static' },
        {
          title: 'Confirmation',
          body: `Would you like to delete ${guestData.email} from your list of saved guests?`,
          isLoading: false,
          options: {
            buttonCancel: true,
            buttonCancelText: 'No Thanks',
            buttonOk: true,
            buttonOkText: 'Ok'
          }
        }
      )
      .then((response: boolean) => {
        if (response) {
          this.subscribe(
            this.userService.deleteGuest(guestData.id),
            (resp) => {
              if (resp) {
                this.savedGuests.splice(this.savedGuests.indexOf(guestData), 1);
              }
            },
            (err) => {
              console.log(err.message);
            }
          );
        }
      });
  }

  chooseOffice(office: string) {
    const changedOffice = office === '_none' ? null : office;
    this.selectedOffice = office;
    this.getGuests(changedOffice);
  }
}
